import {
  EXPLORE_COLLECTION_SEARCH_CONTEXT,
  EXPLORE_INSPIRATION_SEARCH_CONTEXT,
  EXPLORE_TRIP_SEARCH_CONTEXT,
  EXPLORE_TYPE_ALL,
  EXPLORE_TYPES,
} from "@/constants/explore"
import { ClientRateLimitException } from "@/types/client-exceptions"
import { CollectionRef } from "@/types/collection"
import { InspirationRef, PlaceRef } from "@/types/entity"
import {
  ExploreLocation,
  ExplorePlaceType,
  ExploreType,
  InitExploreProps,
} from "@/types/explore"
import { Location, LocationSearchHit, PlaceSummary } from "@/types/place"
import { TripRef } from "@/types/trip"
import { BoundingBox } from "@/types/util"
import { Honeybadger } from "@honeybadger-io/react"
import { createAsyncThunk } from "@reduxjs/toolkit"
import uniqBy from "lodash/uniqBy"

import { buildClientAxios } from "@/lib/client-axios"
import { httpIsCancelled } from "@/lib/exception-utils"
import { fetchExploreProps } from "@/lib/explore"
import { toastManager } from "@/lib/toast"
import { isTouch } from "@/lib/utils/dom"
import { singleParam } from "@/lib/utils/url"
import {
  sendAnalyticsEvent,
  setAnalyticsUserProperties,
} from "../analytics/amplitude-utils"
import { ExploreApi } from "../explore-api"
import { ExploreStreamListener } from "../explore-stream-listener"
import { InspirationApi } from "../inspiration-api"
import { processRelatedPlaceholderData } from "../place-utils"
import { webStorage } from "../web-storage"
import { placeApi } from "./api/place-api"
import { selectOrgExploreLocationRef } from "./app-slice"
import { showRegisterGateDialog } from "./auth-thunks"
import { selectCurrentCollectionRef } from "./collection-slice"
import { closeDetailSheet, selectSheetCoversSearch } from "./detail-sheet-slice"
import {
  addRecentLocation,
  selectBbox,
  selectConfigurations,
  selectExploreFilters,
  selectIsExploringMapArea,
  selectIsGuides,
  selectIsLocationUserSelected,
  selectLocation,
  selectUnifiedSearchContext,
  setAutoDetectingLocation,
  setBbox,
  setConfiguration,
  setGuides,
  setIsExploringMapArea,
  setIsResolvingPositionLocation,
  setLoadingGuides,
  setLoadingResults,
  setLocation,
  setLocationGuides,
  setNearbyPlaces,
  setPagination,
  setPinnedTypeaheadPlace,
  setPlaces,
  setQueryId,
  setSearchString,
  setSelectedType,
  setSuggestedTabOrder,
  setUserRateLimited,
} from "./explore-slice"
import { geoStartRequested } from "./geolocation-actions"
import {
  selectDisplayPosition,
  selectGeoPermission,
  selectGeoStatus,
  selectLivePosition,
} from "./geolocation-selectors"
import { initGeo } from "./geolocation-thunks"
import { selectCurrInspirationRef } from "./inspiration-slice"
import { setHoveredPlace } from "./map-hover-slice"
import { AppDispatch, RootState } from "./redux-store"
import { selectTripRef } from "./trip-slice"
import userSlice, { selectCurrentLocationEntityRef } from "./user-slice"

const exploreApi = new ExploreApi(buildClientAxios())
const inspirationApi = new InspirationApi(buildClientAxios())

type AbortAction = { abort: () => void }

let fetchPlacesAction: AbortAction | undefined = undefined
let fetchGuidesAction: AbortAction | undefined = undefined
let prevGuidesLocationRef: PlaceRef | undefined = undefined

/**
 * Decides whether to defer location to explore-map (geo path) or fetch IP
 * location now. Callers must `await initGeo` first so `geoPermission` isn't
 * "unknown".
 *
 * - willUseGeo: defer to explore-map
 * - shouldStartGeo: dispatch `geoStartRequested` now
 */
function shouldDeferToGeoLocation(state: RootState, hasLocQuery: boolean) {
  const isLocationUserSelected = selectIsLocationUserSelected(state)
  const geoPermission = selectGeoPermission(state)
  const geoStatus = selectGeoStatus(state)
  const geoPosition = selectDisplayPosition(state)
  const isTouchDevice = isTouch()

  // If URL specifies location or user already selected one, use that
  if (hasLocQuery || isLocationUserSelected) {
    return { willUseGeo: false, shouldStartGeo: false }
  }

  const hasRestoredPosition =
    geoPosition && (geoStatus === "restored" || geoStatus === "watching")

  // Touch devices: prompt for location unless permission denied
  if (isTouchDevice && geoPermission !== "denied") {
    return { willUseGeo: true, shouldStartGeo: true }
  }

  // Non-touch devices: use restored position if available
  if (
    !isTouchDevice &&
    hasRestoredPosition &&
    (geoPermission === "granted" || geoPermission === "unknown")
  ) {
    return { willUseGeo: true, shouldStartGeo: false }
  }

  // Permission already granted: silently acquire a fresh fix (fixes the
  // missing marker for returning visitors whose session storage was wiped).
  if (geoPermission === "granted") {
    return { willUseGeo: true, shouldStartGeo: true }
  }

  return { willUseGeo: false, shouldStartGeo: false }
}

export const initExplore = createAsyncThunk<
  void,
  InitExploreProps,
  { dispatch: AppDispatch; state: RootState }
>("exploreThunks/initExplore", async (initProps, { dispatch, getState }) => {
  const hasUrlLocQuery = Boolean(initProps.router.query.loc)

  // Check for a session-persisted location (user previously selected one)
  const sessionLocation = !hasUrlLocQuery
    ? webStorage.tryGetJSON<ExploreLocation>("exploreSessionLocation", {
        type: "session",
      })
    : null

  const orgLocationRef = selectOrgExploreLocationRef(getState())
  const hasLocQuery = hasUrlLocQuery || Boolean(orgLocationRef)

  // Wait for geo init so `geoPermission` is known before we decide. Otherwise
  // we'd fetch IP location and immediately overwrite it with a GPS fix (flash + double fetch).
  await dispatch(initGeo())

  const { willUseGeo, shouldStartGeo } = shouldDeferToGeoLocation(
    getState(),
    hasLocQuery || Boolean(sessionLocation)
  )

  const queryTypeParam = singleParam(initProps.router.query.type)
  const selectedType =
    queryTypeParam && EXPLORE_TYPES.includes(queryTypeParam as ExploreType)
      ? (queryTypeParam as ExploreType)
      : EXPLORE_TYPE_ALL
  const querySearchParam = singleParam(initProps.router.query.search) || ""

  // Start geolocation before setting location when permission allows it.
  // This ensures geoStatus is "acquiring" so UI shows "Locating..."
  if (shouldStartGeo) {
    dispatch(geoStartRequested({ reason: "user" }))
  }

  // Set auto-detecting flag so UI shows "Locating..." instead of IP location
  if (willUseGeo) {
    dispatch(setAutoDetectingLocation(true))
  }

  // Set selected type and search string from URL before setting location/fetching.
  // This prevents client-route-handler from later dispatching these actions
  // which would trigger explore-content to fetch again.
  dispatch(setSelectedType(selectedType))
  dispatch(setSearchString(querySearchParam))

  sendAnalyticsEvent({
    name: "ViewExplorePage",
    properties: { type: selectedType },
  })

  if (willUseGeo) {
    // Clear existing places to show loading state while waiting for geo
    dispatch(setPlaces([]))
    dispatch(setGuides([]))
    dispatch(setLoadingResults(true))
  } else if (sessionLocation) {
    // Restore user-selected location from session storage
    dispatch(setLocation({ location: sessionLocation, isUserSelected: true }))
    void dispatch(fetchData())
  } else {
    const { location } = await fetchExploreProps(
      buildClientAxios(),
      hasUrlLocQuery ? initProps.router.query : { loc: orgLocationRef }
    )
    dispatch(
      setLocation({
        location,
        isUserSelected: hasUrlLocQuery,
      })
    )
    void dispatch(fetchData())
  }
})

export const initLocationFromIp = createAsyncThunk<
  void,
  InitExploreProps,
  { dispatch: AppDispatch; state: RootState; rejectValue: "fetch_failed" }
>(
  "exploreThunks/initLocationFromIp",
  async ({ router }, { dispatch, getState, rejectWithValue }) => {
    // Skip the network call entirely if a location is already set or pending.
    const hasLocQuery = Boolean(router.query.loc)
    if (
      selectLocation(getState()) ||
      selectIsLocationUserSelected(getState()) ||
      hasLocQuery
    ) {
      return
    }

    try {
      const { location } = await fetchExploreProps(
        buildClientAxios(),
        router.query
      )
      // Re-check after the await so we don't clobber a location set meanwhile.
      if (
        selectLocation(getState()) ||
        selectIsLocationUserSelected(getState())
      ) {
        return
      }

      dispatch(
        setLocation({
          location,
          isUserSelected: false,
        })
      )
    } catch (e) {
      Honeybadger.notify(`initLocationFromIp failed: ${JSON.stringify(e)}`)
      return rejectWithValue("fetch_failed")
    }
  }
)

/**
 * Fallback when geolocation isn't usable. Acquires an IP location if needed
 * (no-op if one is already set) and always triggers a data fetch so the
 * loading shimmer clears. Toasts on IP-lookup failure with no prior location.
 */
export const fallbackToIpLocation = createAsyncThunk<
  void,
  InitExploreProps,
  { dispatch: AppDispatch; state: RootState }
>(
  "exploreThunks/fallbackToIpLocation",
  async ({ router }, { dispatch, getState }) => {
    const result = await dispatch(initLocationFromIp({ router }))
    // Only treat as a hard failure if we have no location to fall back on.
    if (
      initLocationFromIp.rejected.match(result) &&
      !selectLocation(getState())
    ) {
      toastManager.add({
        type: "error",
        description:
          "We couldn’t auto-detect your location. Please pick one to explore.",
      })
      dispatch(setLoadingResults(false))
      return
    }
    void dispatch(fetchData())
  }
)

export const fetchPlaces = createAsyncThunk<
  void,
  { bbox?: BoundingBox; page?: number; locationOverride?: ExploreLocation },
  {
    state: RootState
    dispatch: AppDispatch
    signal: AbortSignal
  }
>(
  "exploreThunks/fetchPlaces",
  async (
    { bbox, page, locationOverride },
    { getState, dispatch, abort, signal }
  ) => {
    fetchPlacesAction?.abort()
    fetchPlacesAction = { abort }
    ExploreStreamListener.closeActiveListeners()

    const state = getState()

    const { selectedType, searchString, socialFilters } = state.exploreSlice
    const location = locationOverride ?? state.exploreSlice.location
    const filters = selectExploreFilters(state)
    const livePosition = selectLivePosition(state)
    const currentLocationRef = selectCurrentLocationEntityRef(state)
    const unifiedSearchContext = selectUnifiedSearchContext(state)

    // This allows the backend to filter out places already added to the trip/guide/collection
    let contextRef: TripRef | InspirationRef | CollectionRef | undefined
    if (unifiedSearchContext === EXPLORE_TRIP_SEARCH_CONTEXT) {
      contextRef = selectTripRef(state) ?? undefined
    } else if (unifiedSearchContext === EXPLORE_INSPIRATION_SEARCH_CONTEXT) {
      contextRef = selectCurrInspirationRef(state) ?? undefined
    } else if (unifiedSearchContext === EXPLORE_COLLECTION_SEARCH_CONTEXT) {
      contextRef = selectCurrentCollectionRef(state) ?? undefined
    }

    // only send exact coordinates when exploring the user's current location
    // and we have a live (trusted) position
    const shouldSendExactPosition =
      livePosition &&
      currentLocationRef &&
      location?.entity_ref === currentLocationRef

    const pageIndex = page || 1

    if (!location) {
      dispatch(setLoadingResults(false))
      return
    }

    dispatch(setPlaces([]))
    dispatch(setLoadingResults(true))
    dispatch(setPagination(undefined))

    dispatch(setIsExploringMapArea(!!bbox))

    try {
      const limit =
        selectedType === "all" ? 8 : selectedType === "location" ? 12 : 18

      const { stream_id, query_id, place_type, suggested_tab_order } =
        await exploreApi.fetchExplorePlacesStream(
          selectedType,
          bbox || location.entity_ref,
          searchString,
          limit,
          pageIndex,
          shouldSendExactPosition ? livePosition : undefined,
          filters,
          socialFilters,
          contextRef,
          { signal }
        )

      dispatch(setQueryId(query_id))

      if (suggested_tab_order) {
        dispatch(setSuggestedTabOrder(suggested_tab_order))
      }

      // only auto-switch type when on "all" tab - once a specific type is selected, respect it
      if (place_type && place_type !== selectedType && selectedType === "all") {
        dispatch(setSelectedType(place_type))
        dispatch(setLoadingResults(false))
        return
      }

      ExploreStreamListener.listenToStream(stream_id, dispatch)
    } catch (e) {
      if (!httpIsCancelled(e)) {
        if (e instanceof ClientRateLimitException) {
          setAnalyticsUserProperties({
            type: "rateLimitProps",
            rateLimit: { reason: e.data.reason },
          })
          if (
            e.data.reason === "anon_throttling" ||
            e.data.reason === "unverified_throttling"
          ) {
            setAnalyticsUserProperties({
              type: "registerGateProps",
              where: "explore",
            })
            void dispatch(showRegisterGateDialog("explore"))
          } else if (e.data.reason === "abuse_throttling") {
            dispatch(setUserRateLimited(true))
          }
        } else {
          console.error("Failed to fetch places", e)
          toastManager.add({
            type: "error",
            description:
              "Sorry, we had trouble loading places. Please try again.",
          })
          dispatch(setPlaces([]))
          dispatch(setLoadingResults(false))
        }
      }
    }
  }
)

export const initLocationFromCurrentPosition = createAsyncThunk<
  void,
  { latitude: number; longitude: number },
  { dispatch: AppDispatch; state: RootState }
>(
  "exploreThunks/initLocationFromCurrentPosition",
  async ({ latitude, longitude }, { dispatch }) => {
    // Drives the "Locating..." title via selectIsAutoDetectingLocation so
    // callers don't have to manage the flag themselves.
    dispatch(setIsResolvingPositionLocation(true))
    try {
      const newLocation = await exploreApi.fetchLocationByLatLong(
        latitude,
        longitude
      )
      // Auto-detected from GPS - not user selected, preserve any search from URL
      await dispatch(
        initExploreLocation({
          location: newLocation,
          isUserSelected: false,
          preserveSearch: true,
        })
      ).unwrap()
      dispatch(userSlice.actions.setCurrentLocation(newLocation))
    } catch (e) {
      const msg = "Failed to get location using lat/long"
      console.error(msg, e)
      toastManager.add({ type: "error", description: msg })
    } finally {
      dispatch(setIsResolvingPositionLocation(false))
    }
  }
)

const fetchGuidesForLocation = createAsyncThunk<
  void,
  { location: ExploreLocation; signal?: AbortSignal },
  { dispatch: AppDispatch; state: RootState }
>(
  "exploreThunks/fetchGuidesForLocation",
  async ({ location, signal }, { dispatch }) => {
    if (location.entity_ref === prevGuidesLocationRef) {
      return
    }

    const inspirationsRet = await inspirationApi.fetchInspirationsForPlace(
      location.entity_ref,
      { signal }
    )
    dispatch(setLocationGuides(inspirationsRet))
    prevGuidesLocationRef = location.entity_ref
  }
)

export const fetchGuides = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>(
  "exploreThunks/fetchGuides",
  async (_, { dispatch, getState, abort, signal }) => {
    fetchGuidesAction?.abort()
    fetchGuidesAction = { abort }

    const { location, locationGuides } = getState().exploreSlice
    if (!location) return

    if (
      prevGuidesLocationRef === location.entity_ref &&
      locationGuides.length > 0
    )
      return

    dispatch(setLoadingGuides(true))

    try {
      await dispatch(fetchGuidesForLocation({ location, signal }))
    } catch (error) {
      if (!httpIsCancelled(error)) {
        Honeybadger.notify(
          `Error fetching inspirations: ${JSON.stringify(error)}`
        )
        dispatch(setGuides([]))
      }
    } finally {
      dispatch(setLoadingGuides(false))
    }
  }
)

export const fetchData = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>("exploreThunks/fetchData", async (params, { dispatch, getState }) => {
  fetchPlacesAction?.abort()
  fetchGuidesAction?.abort()
  ExploreStreamListener.closeActiveListeners()

  const isExploringMapArea = selectIsExploringMapArea(getState())
  const bbox = selectBbox(getState())

  if (selectIsGuides(getState())) {
    await dispatch(fetchGuides())
  } else {
    await dispatch(
      fetchPlaces({ bbox: isExploringMapArea && bbox ? bbox : undefined })
    )
  }
})

export const handleTypeChange = createAsyncThunk<
  void,
  ExploreType,
  { dispatch: AppDispatch; state: RootState }
>("exploreThunks/handleTypeChange", async (type, { dispatch, getState }) => {
  dispatch(setPlaces([]))
  dispatch(setNearbyPlaces([]))
  dispatch(setSelectedType(type))
  dispatch(setPinnedTypeaheadPlace(undefined))
  dispatch(setLoadingResults(false))
  if (selectSheetCoversSearch(getState())) {
    void dispatch(closeDetailSheet())
  }
})

export const initExploreLocation = createAsyncThunk<
  void,
  {
    location: Location | LocationSearchHit
    isUserSelected?: boolean
    preserveSearch?: boolean
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "exploreThunks/initExploreLocation",
  async (
    { location, isUserSelected = true, preserveSearch = false },
    { dispatch, getState }
  ) => {
    const prevLocation = selectLocation(getState())
    const isExploringMapArea = selectIsExploringMapArea(getState())
    if (
      location.entity_ref === prevLocation?.entity_ref &&
      !isExploringMapArea
    ) {
      return
    }
    dispatch(setGuides([]))
    dispatch(setLocationGuides([]))
    dispatch(setPlaces([]))
    dispatch(setPinnedTypeaheadPlace(undefined))
    dispatch(setHoveredPlace(undefined))
    if (!preserveSearch) {
      dispatch(setSearchString(""))
    }
    dispatch(setIsExploringMapArea(false))
    dispatch(setBbox(undefined))
    dispatch(setPagination(undefined))
    dispatch(setSuggestedTabOrder(undefined))
    dispatch(setLocation({ location, isUserSelected }))
    dispatch(addRecentLocation(location))
  }
)

export const handleSearchChange = createAsyncThunk<
  void,
  string,
  { dispatch: AppDispatch; state: RootState }
>(
  "exploreThunks/handleSearchChange",
  async (newSearchString, { dispatch, getState }) => {
    const isExploringMapArea = selectIsExploringMapArea(getState())
    const unifiedSearchContext = selectUnifiedSearchContext(getState())
    const bbox = selectBbox(getState())

    dispatch(setSearchString(newSearchString))
    dispatch(setSelectedType("all"))
    dispatch(setPinnedTypeaheadPlace(undefined))

    if (newSearchString) {
      sendAnalyticsEvent({
        name: "ExploreSearchSubmit",
        properties: { search: newSearchString, context: unifiedSearchContext },
      })
    }
    await dispatch(fetchPlaces({ bbox: isExploringMapArea ? bbox : undefined }))
  }
)

export const selectTypeaheadPlace = createAsyncThunk<
  void,
  PlaceSummary,
  { dispatch: AppDispatch; state: RootState }
>(
  "exploreThunks/selectTypeaheadPlace",
  async (place, { dispatch, getState }) => {
    // PlaceType is a superset of ExploreType (it has "activity" and "airport"
    // which have no tab). Skip pinning for those and fall back to a normal
    // text search under the "all" tab.
    if (place.type === "activity" || place.type === "airport") {
      // handleSearchChange clears the pin and runs an "all" text search.
      await dispatch(handleSearchChange(place.name))
      return
    }

    dispatch(setPinnedTypeaheadPlace(place))
    dispatch(setSelectedType(place.type))
    dispatch(setSearchString(place.name))
    dispatch(setNearbyPlaces([]))
    dispatch(setBbox(undefined))
    if (selectSheetCoversSearch(getState())) {
      void dispatch(closeDetailSheet())
    }
    await dispatch(fetchPlaces({}))
  }
)

export const selectTypeaheadPlaceRef = createAsyncThunk<
  void,
  PlaceRef,
  { dispatch: AppDispatch; state: RootState }
>("exploreThunks/selectTypeaheadPlaceRef", async (placeRef, { dispatch }) => {
  let place: PlaceSummary
  try {
    place = await dispatch(
      placeApi.endpoints.getPlace.initiate({ placeRef }, { subscribe: false })
    ).unwrap()
  } catch {
    toastManager.add({
      type: "error",
      description: "Place not found. Please try again.",
    })
    return
  }
  await dispatch(selectTypeaheadPlace(place))
})

export const fetchRelatedPlaces = createAsyncThunk<
  void,
  { placeRefs: PlaceRef[] },
  { dispatch: AppDispatch; state: RootState }
>("exploreThunks/fetchRelatedPlaces", async ({ placeRefs }, { dispatch }) => {
  try {
    const fetchPromises = placeRefs.map((ref) =>
      dispatch(placeApi.endpoints.getRelatedPlaceMarkers.initiate(ref))
        .unwrap()
        .then((response) => {
          if (
            response &&
            ((response.similar && response.similar.length > 0) ||
              Object.keys(response.nearby || {}).length > 0)
          ) {
            return processRelatedPlaceholderData(response)
          }
          return []
        })
        .catch((error) => {
          console.error(`Error fetching related places data for ${ref}:`, error)
          return []
        })
    )

    const results = await Promise.all(fetchPromises)
    const uniquePlaces = uniqBy(results.flat(), "entity_ref")
    dispatch(setNearbyPlaces(uniquePlaces))
  } catch (error) {
    if (
      error instanceof ClientRateLimitException &&
      (error.data.reason === "anon_throttling" ||
        error.data.reason === "unverified_throttling")
    ) {
      setAnalyticsUserProperties({
        type: "rateLimitProps",
        rateLimit: { reason: error.data.reason },
      })
      void dispatch(showRegisterGateDialog("explore"))
    } else {
      throw error
    }
  }
})

export const fetchExploreConfiguration = createAsyncThunk<
  void,
  ExplorePlaceType,
  { state: RootState; dispatch: AppDispatch }
>(
  "exploreThunks/fetchExploreConfiguration",
  async (placeType, { getState, dispatch }) => {
    // Check if already cached
    const configurations = selectConfigurations(getState())
    if (configurations[placeType]) {
      return
    }

    try {
      const configuration =
        await exploreApi.fetchExploreConfiguration(placeType)
      dispatch(setConfiguration(configuration))
    } catch (error) {
      console.error(`Failed to fetch configuration for ${placeType}:`, error)
      // Don't throw - silently fail and use hardcoded defaults if available
    }
  }
)
